import type { FillInTheBlankBlock, SourceLanguageCode } from '@ellie/shared';
import { BlockHeader } from '../BlockHeader';
import { NativeHint } from '../NativeHint';

interface Props {
  block: FillInTheBlankBlock;
  index: number;
  nativeLanguage: SourceLanguageCode;
  targetLanguage: string;
  pageNumber: number;
}

/**
 * Renders the sentence text, replacing patterns like `___` or `_.{3,}`
 * with a styled blank line.
 */
function renderSentenceText(text: string) {
  // Split on underscores (3+ underscores or the literal blank marker)
  const parts = text.split(/_{3,}/);
  if (parts.length <= 1) {
    return <>{text}</>;
  }

  return (
    <>
      {parts.map((part, i) => (
        <span key={i}>
          {part}
          {i < parts.length - 1 && <span className="wb-fill-blank" />}
        </span>
      ))}
    </>
  );
}

export function FillInTheBlankPreview({
  block,
  index,
  nativeLanguage,
  targetLanguage,
  pageNumber,
}: Props) {
  return (
    <div className="wb-page">
      <BlockHeader block={block} index={index} />

      <div className="wb-block-instructions">
        <span className="wb-icon">{'\u{270F}\u{FE0F}'}</span>
        <div>
          <p>{block.instructions}</p>
          <NativeHint
            text={block.instructionsNative}
            nativeLanguage={nativeLanguage}
            label={targetLanguage.toUpperCase()}
          />
        </div>
      </div>

      {/* Word bank */}
      <div className="wb-word-bank">
        <div className="wb-word-bank-label">Word Bank</div>
        {block.wordBank.map((word, i) => (
          <span key={i} className="wb-word-chip">
            {word}
          </span>
        ))}
      </div>

      {/* Sentences */}
      <div className="wb-fill-sentences">
        {block.sentences.map((sentence, i) => (
          <div key={i} className="wb-fill-sentence">
            <p className="wb-fill-text">
              <span className="wb-fill-number">{i + 1}</span>
              {renderSentenceText(sentence.text)}
            </p>
            <div className="wb-fill-hint">
              <NativeHint text={sentence.hint} nativeLanguage={nativeLanguage} />
            </div>
          </div>
        ))}
      </div>

      <span className="wb-page-number">{pageNumber}</span>
    </div>
  );
}
